import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import type { BillingSavedView } from '../../../store';
import type { WorkViewKey as ClaimWorkViewKey } from '../../../types/encounterWork';
import { jotBillStickyTop } from '../jotBillHeaderHeight';

import { CLAIM_WORK_VIEW_GROUPS } from './claimWorkViewGroups';

export interface ClaimWorkViewRailProps {
  activeSavedViewId: string | null;
  activeWorkView: ClaimWorkViewKey;
  counts: Record<string, number>;
  /** No hidden-excluded read has landed yet, so no number is honest. */
  countsPending?: boolean;
  dirty: boolean;
  isMutating: boolean;
  savedViews: BillingSavedView[];
  onDelete: (view: BillingSavedView) => void;
  onSave: () => void;
  onSelectSavedView: (view: BillingSavedView) => void;
  onSelectWorkView: (key: ClaimWorkViewKey) => void;
  onUpdate: () => void;
}

/**
 * The claim-work rail: four queues, then three lists (GDN-3381).
 *
 * The two groups are the product's promise made visible. A QUEUE is work
 * someone still owes an answer on, so it honours archive and snooze; a LIST
 * is a lens over the same claims and never hides anything. Reading the rail
 * should be enough to know which of the two you are standing in, which is
 * why the group labels are here and not only in the docs.
 *
 * Counts come from `work_view_counts` with hidden rows excluded, so a queue's
 * number is the work outstanding, not the work plus the work someone already
 * decided about.
 */
export const ClaimWorkViewRail = ({
  activeSavedViewId,
  activeWorkView,
  counts,
  countsPending,
  dirty,
  isMutating,
  savedViews,
  onDelete,
  onSave,
  onSelectSavedView,
  onSelectWorkView,
  onUpdate,
}: ClaimWorkViewRailProps) => (
  <Card
    variant="outlined"
    /* Sticks BELOW the page header/tabs, not under them: the offset is the
       shell's MEASURED header height (GDN-3214), so editing the header can
       never silently regress this. */
    sx={{ position: { lg: 'sticky' }, top: { lg: jotBillStickyTop(16) }, width: '100%' }}
  >
    <CardContent sx={{ '&:last-child': { pb: 1.5 }, p: 1.5 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em' }}>
        CLAIM WORK
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.25 }} variant="caption">
        One queue per claim · lists never hide
      </Typography>
      {CLAIM_WORK_VIEW_GROUPS.map((group, groupIndex) => (
        <Box key={group.label} sx={{ mt: groupIndex === 0 ? 0 : 1.25 }}>
          <Typography
            color="text.secondary"
            component="div"
            id={`claim-work-group-${group.label.toLowerCase()}`}
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              mb: 0.5,
              px: 1,
              textTransform: 'uppercase',
            }}
          >
            {group.label}
          </Typography>
          <Stack
            aria-labelledby={`claim-work-group-${group.label.toLowerCase()}`}
            component="nav"
            spacing={0.4}
          >
            {group.views.map(view => {
              const selected = activeSavedViewId === null && activeWorkView === view.key;
              return (
                <Button
                  key={view.key}
                  aria-pressed={selected}
                  color="inherit"
                  onClick={() => onSelectWorkView(view.key)}
                  size="small"
                  sx={{
                    '&::before': {
                      bgcolor: view.color,
                      borderRadius: 4,
                      content: '""',
                      height: selected ? 20 : 7,
                      mr: 1,
                      transition: 'height 120ms ease',
                      width: 3,
                    },
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    justifyContent: 'flex-start',
                    minHeight: 36,
                    px: 1,
                    textTransform: 'none',
                  }}
                >
                  <Box
                    component="span"
                    sx={{ flex: 1, overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis' }}
                  >
                    {view.label}
                  </Box>
                  <Typography
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    variant="caption"
                  >
                    {countsPending ? '' : (counts[view.key] ?? 0)}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
        </Box>
      ))}

      <Divider sx={{ my: 1.25 }} />
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography color="text.secondary" variant="caption">
          Organization views
        </Typography>
        <Button
          onClick={onSave}
          size="small"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </Stack>
      <Stack spacing={0.4} sx={{ mt: 0.5 }}>
        {savedViews.length === 0 && (
          <Typography color="text.secondary" sx={{ px: 1, py: 1 }} variant="caption">
            Save a filter set for everyone in this organization.
          </Typography>
        )}
        {savedViews.map(view => {
          const selected = activeSavedViewId === view.billing_saved_view_id;
          return (
            <Box key={view.billing_saved_view_id} sx={{ display: 'flex', gap: 0.25 }}>
              <Button
                aria-pressed={selected}
                color="inherit"
                onClick={() => onSelectSavedView(view)}
                size="small"
                startIcon={<SaveOutlinedIcon fontSize="small" />}
                sx={{
                  bgcolor: selected ? 'action.selected' : 'transparent',
                  color: selected ? 'primary.main' : 'text.primary',
                  flex: 1,
                  justifyContent: 'flex-start',
                  minWidth: 0,
                  px: 1,
                  textTransform: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{ flex: 1, overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis' }}
                >
                  {view.name}
                </Box>
                <Typography
                  color="text.secondary"
                  sx={{ ml: 0.5, fontVariantNumeric: 'tabular-nums' }}
                  variant="caption"
                >
                  {view.matching_count ?? 0}
                </Typography>
              </Button>
              {selected && (
                <Button
                  aria-label={`Delete ${view.name}`}
                  color="inherit"
                  disabled={isMutating}
                  onClick={() => onDelete(view)}
                  size="small"
                  sx={{ minWidth: 32, px: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
      {activeSavedViewId && dirty && (
        <Alert
          action={
            <Button color="inherit" disabled={isMutating} onClick={onUpdate} size="small">
              Update
            </Button>
          }
          icon={false}
          severity="warning"
          sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.72rem' } }}
        >
          Unsaved changes
        </Alert>
      )}
    </CardContent>
  </Card>
);
